
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import orderService from './orderService'
import { toast } from 'react-toastify'
import { PDFDocument } from 'pdf-lib';

const initialState = {
  orders: [],
  labels: [],
  review: null,
  order_doc: null,
  label: null,
  order: null,
  pagination: {
    total: 0,
    page: 1,
    limit: 100,
    totalPages: 1,
  },
  isError: false,
  isSuccess: false,
  isLoading: false,
  isShippingLabelLoading: false,
  isInvoiceLoading: false,
  message: '',
  warningShown: false, // Add a flag to track if the warning has been shown
}

// Get all orders
export const getOrders = createAsyncThunk('orders/getAll', async (params = {}, thunkAPI) => {
    try {
        const token = thunkAPI.getState().auth.user.token
        return await orderService.getOrders(token, params)
    } catch (error) {
        const message =
            (error.response && error.response.data && error.response.data.message) ||
            error.message ||
            error.toString()
        return thunkAPI.rejectWithValue(message)
    }
})

// Get unpaid orders
export const getUnpaidOrders = createAsyncThunk('orders/getUnpaid', async (params = {}, thunkAPI) => {
    try {
        const token = thunkAPI.getState().auth.user.token
        return await orderService.getUnpaidOrders(token, params)
    } catch (error) {
        const message =
            (error.response && error.response.data && error.response.data.message) ||
            error.message ||
            error.toString()
        return thunkAPI.rejectWithValue(message)
    }
})

// Get order by id
export const getOrder = createAsyncThunk('orders/getOne', async (orderId, thunkAPI) => {
    try {
        const token = thunkAPI.getState().auth.user.token
        return await orderService.getOrder(token, orderId)
    } catch (error) {
        const message =
            (error.response && error.response.data && error.response.data.message) ||
            error.message ||
            error.toString()
        return thunkAPI.rejectWithValue(message)
    }
})

export const fetchOrderShippingLabel = createAsyncThunk('orders/getOneShippingLabel', async (orderId, thunkAPI) => {
    try {
        const token = thunkAPI.getState().auth.user.token;
        return await orderService.getOrderShippingLabel(token, orderId);
    } catch (error) {
        const message =
            (error.response && error.response.data && error.response.data.message) ||
            error.message ||
            error.toString();
        return thunkAPI.rejectWithValue(message);
    }
});

export const fetchOrderInvoice = createAsyncThunk('orders/getOneInvoice', async (orderId, thunkAPI) => {
    try {
        const token = thunkAPI.getState().auth.user.token;
        return await orderService.getOrderInvoice(token, orderId);
    } catch (error) {
        const message =
            (error.response && error.response.data && error.response.data.message) ||
            error.message ||
            error.toString();
        return thunkAPI.rejectWithValue(message);
    }
});

// Get order public
export const getOrderPublic = createAsyncThunk('orders/getOnePublic', async (orderId, thunkAPI) => {
    try {
        return await orderService.getOrderPublic(orderId)
    } catch (error) {
        const message =
            (error.response && error.response.data && error.response.data.message) ||
            error.message ||
            error.toString()
        return thunkAPI.rejectWithValue(message)
    }
});

// Poll MakeCommerce status to speed up Processing page
export const checkPaymentStatus = createAsyncThunk('orders/checkPaymentStatus', async (orderId, thunkAPI) => {
    try {
        return await orderService.checkPaymentStatus(orderId);
    } catch (error) {
        const message =
            (error.response && error.response.data && error.response.data.message) ||
            error.message ||
            error.toString();
        return thunkAPI.rejectWithValue(message);
    }
});

// Initiate MakeCommerce payment session
export const initiatePayment = createAsyncThunk('orders/initiatePayment', async (orderData, thunkAPI) => {
    try {
        return await orderService.initiatePayment(orderData);
    } catch (error) {
        const message =
            (error.response && error.response.data && error.response.data.message) ||
            error.message ||
            error.toString();
        return thunkAPI.rejectWithValue(message);
    }
});

// Toggle status
export const toggleStatus = createAsyncThunk('orders/toggleStatus', async (orderData, thunkAPI) => {
    try {
        const token = thunkAPI.getState().auth.user.token;
        return await orderService.toggleStatus(token, orderData);
    } catch (error) {
        const message =
            (error.response && error.response.data && error.response.data.message) ||
            error.message ||
            error.toString();
        return thunkAPI.rejectWithValue(message);
    }
});

// Toggle status
export const toggleStatusMultiple = createAsyncThunk('orders/toggleStatus/multiple', async (orderData, thunkAPI) => {
    try {
        const token = thunkAPI.getState().auth.user.token;
        return await orderService.toggleStatus(token, orderData);
    } catch (error) {
        const message =
            (error.response && error.response.data && error.response.data.message) ||
            error.message ||
            error.toString();
        return thunkAPI.rejectWithValue(message);
    }
});

// Contact us
export const contactUs = createAsyncThunk('orders/contactUs', async (contactData, thunkAPI) => {
    try {
        return await orderService.contactUs(contactData);
    } catch (error) {
        const message =
            (error.response && error.response.data && error.response.data.message) ||
            error.message ||
            error.toString();
        return thunkAPI.rejectWithValue(message);
    }
});

// Resend email
export const resendEmail = createAsyncThunk('orders/resendEmail', async (orderId, thunkAPI) => {
    try {
        return await orderService.resendEmail(orderId);
    } catch (error) {
        const message =
            (error.response && error.response.data && error.response.data.message) ||
            error.message ||
            error.toString();
        return thunkAPI.rejectWithValue(message);
    }
});

export const retryFulfillment = createAsyncThunk('orders/retryFulfillment', async (orderId, thunkAPI) => {
    try {
        const token = thunkAPI.getState().auth.user.token;
        return await orderService.retryFulfillment(token, orderId);
    } catch (error) {
        const message =
            (error.response && error.response.data && error.response.data.message) ||
            error.message ||
            error.toString();
        return thunkAPI.rejectWithValue(message);
    }
});

// Fetch shipping labels for selected orders
export const fetchShippingLabels = createAsyncThunk('orders/fetchShippingLabels', async (ids, thunkAPI) => {
    try {
        const token = thunkAPI.getState().auth.user.token;
        const payload = await orderService.getShippingLabels(token, ids);
        const data = payload || {};
        const labels = Array.isArray(data) ? data : (data.labels || []);
        const failures = Array.isArray(data) ? [] : (data.failures || []);

        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        const fileName = `cela-kartes-${timestamp}.pdf`;

        if (failures.length > 0) {
            const msg = currentLang === 'en'
                ? `Some labels failed (${failures.length}). Downloading the rest.`
                : `Daļu ceļa karšu neizdevās izveidot (${failures.length}). Lejupielādēju pārējās.`;
            toast.warn(msg);
        }

        if (labels.length === 0) {
            throw new Error(currentLang === 'en' ? 'No labels were created.' : 'Neizdevās izveidot nevienu ceļa karti.');
        }

        await combineAndDownloadPdfs(labels, fileName);
        return { labels, failures };
    } catch (error) {
        const message =
            (error.response && error.response.data && error.response.data.message) ||
            error.message ||
            error.toString();
        return thunkAPI.rejectWithValue(message);
    }
});

// Fetch shipping labels for selected orders
export const exportOrders = createAsyncThunk('orders/excel', async (ids, thunkAPI) => {
    try {
        // const token = thunkAPI.getState().auth.user.token;
        return await orderService.exportOrders(ids);
    } catch (error) {
        const message =
            (error.response && error.response.data && error.response.data.message) ||
            error.message ||
            error.toString();
        return thunkAPI.rejectWithValue(message);
    }
});

export const createCustomInvoice = createAsyncThunk('orders/createCustomInvoice', async (data, thunkAPI) => {
    try {
        return await orderService.getCustomInvoice(data);
    } catch (error) {
        const message =
            (error.response && error.response.data && error.response.data.message) ||
            error.message ||
            error.toString();
        return thunkAPI.rejectWithValue(message);
    }
});

export const getReviewObject = createAsyncThunk('orders/getReviewObject', async (reviewId, thunkAPI) => {
    try {
        return await orderService.getReviewObject(reviewId);
    } catch (error) {
        const message =
            (error.response && error.response.data && error.response.data.message) ||
            error.message ||
            error.toString();
        return thunkAPI.rejectWithValue(message);
    }
});

export const postReview = createAsyncThunk('orders/postReview', async (review_data, thunkAPI) => {
    try {
        return await orderService.postReview(review_data);
    } catch (error) {
        const message =
            (error.response && error.response.data && error.response.data.message) ||
            error.message ||
            error.toString();
        return thunkAPI.rejectWithValue(message);
    }
});

export const deleteReview = createAsyncThunk('orders/deleteReview', async (reviewId, thunkAPI) => {
    try {
        const token = thunkAPI.getState().auth.user.token;
        return await orderService.deleteReview(token, reviewId);
    } catch (error) {
        const message =
            (error.response && error.response.data && error.response.data.message) ||
            error.message ||
            error.toString();
        return thunkAPI.rejectWithValue(message);
    }
});

export const updateReviewReply = createAsyncThunk('orders/updateReviewReply', async (data, thunkAPI) => {
    try {
        const token = thunkAPI.getState().auth.user.token;
        console.log('Token:', token); // Log the token
        const response = await orderService.updateReviewReply(token, data);
        return response;
    } catch (error) {
        console.error('Error updating review reply:', error.message); // Log the error
        const message =
            (error.response && error.response.data && error.response.data.message) ||
            error.message ||
            error.toString();
        return thunkAPI.rejectWithValue(message);
    }
});

const downloadExcel = (data, filename) => {
    // Create a Blob from the Excel data (received as a buffer from the backend)
    const blob = new Blob([data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    // Create a link element
    const link = document.createElement('a');
    
    // Create a URL for the Blob and set it as the href attribute of the link
    link.href = window.URL.createObjectURL(blob);
    
    // Set the download attribute to the filename you want
    link.download = filename;

    // Append the link to the document body
    document.body.appendChild(link);

    // Programmatically click the link to trigger the download
    link.click();

    // Clean up by removing the link from the DOM
    document.body.removeChild(link);
};


const downloadPdf = (pdfBytes, fileName = 'pasutijumi.pdf') => {
    // Create a Blob with PDF MIME type
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });

    // Create a URL for the Blob
    const url = window.URL.createObjectURL(blob);

    // Create a link element and trigger the download
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName); // Set the file name
    document.body.appendChild(link);
    link.click(); // Trigger the download
    link.parentNode.removeChild(link); // Clean up
    window.URL.revokeObjectURL(url); // Clean up the URL object
};

const combineAndDownloadPdfs = async (labels, fileName = 'pasutijumi.pdf') => {
    console.log("labels", labels);
    const combinedPdf = await PDFDocument.create();
    let skipped = 0;

    for (const label of labels) {
        if (!label?.shipping_label?.data) {
            skipped += 1;
            continue;
        }

        const data = label.shipping_label.data;

        // Convert byte array to Uint8Array
        const byteArray = new Uint8Array(data);

        // Load the PDF document
        const pdf = await PDFDocument.load(byteArray);

        // Copy pages from the loaded PDF to the combined PDF
        const copiedPages = await combinedPdf.copyPages(pdf, pdf.getPageIndices());
        copiedPages.forEach((page) => combinedPdf.addPage(page));
    }

    if (skipped > 0) {
        toast.warn(currentLang === 'en' ? `Skipped ${skipped} labels due to errors.` : `Izlaistas ${skipped} ceļa kartes kļūdu dēļ.`);
    }

    // Save the combined PDF as a byte array
    const combinedPdfBytes = await combinedPdf.save();

    // Download the combined PDF
    downloadPdf(combinedPdfBytes, fileName);
};

const downloadInvoice = (label, fileName = 'rekins.pdf') => {
    const data = label.invoiceBuffer.data;
    // Convert byte array to Uint8Array
    const byteArray = new Uint8Array(data);

    // Create a Blob with PDF MIME type
    const blob = new Blob([byteArray], { type: 'application/pdf' });

    // Create a URL for the Blob
    const url = window.URL.createObjectURL(blob);

    // Create a link element and trigger the download
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName); // Set the file name
    document.body.appendChild(link);
    link.click(); // Trigger the download
    link.parentNode.removeChild(link); // Clean up
    window.URL.revokeObjectURL(url); // Clean up the URL object
};

const currentLang = localStorage.getItem('lang') || 'lv';

export const orderSlice = createSlice({
    name: 'orders',
    initialState,
    reducers: {
        reset: (state) => {
            state.isLoading = false;
            state.isSuccess = false;
            state.isError = false;
            state.message = '';
            state.isShippingLabelLoading = false;
            state.isInvoiceLoading = false;
            state.warningShown = false; // Reset the warning flag
          },
    },
    extraReducers: (builder) => {
      builder
        .addCase(getOrders.pending, (state) => {
            state.isLoading = true
        })
        .addCase(getOrders.fulfilled, (state, action) => {
            state.isLoading = false
            state.isSuccess = true
            state.orders = action.payload.orders
            state.pagination = {
                total: action.payload.total,
                page: action.payload.page,
                limit: action.payload.limit,
                totalPages: action.payload.totalPages,
            }
            state.order = null
        })
        .addCase(getOrders.rejected, (state, action) => {
            state.isLoading = false
            state.isError = true
            state.message = action.payload
        })
        .addCase(getUnpaidOrders.pending, (state) => {
            state.isLoading = true
        })
        .addCase(getUnpaidOrders.fulfilled, (state, action) => {
            state.isLoading = false
            state.isSuccess = true
            state.orders = action.payload.orders
            state.pagination = {
                total: action.payload.total,
                page: action.payload.page,
                limit: action.payload.limit,
                totalPages: action.payload.totalPages,
            }
        })
        .addCase(getUnpaidOrders.rejected, (state, action) => {
            state.isLoading = false
            state.isError = true
            state.message = action.payload
        })
        .addCase(getOrder.pending, (state) => {
            state.isLoading = true
        })
        .addCase(getOrder.fulfilled, (state, action) => {
            state.isLoading = false
            state.isSuccess = true
            state.order = action.payload
        })
        .addCase(getOrder.rejected, (state, action) => {
            state.isLoading = false
            state.isError = true
            state.message = action.payload
        })
        .addCase(fetchOrderShippingLabel.pending, (state) => {
            state.isShippingLabelLoading = true;
        })
        .addCase(fetchOrderShippingLabel.fulfilled, (state) => {
            state.isShippingLabelLoading = false;
        })
        .addCase(fetchOrderShippingLabel.rejected, (state, action) => {
            state.isShippingLabelLoading = false;
            state.message = action.payload;
        })
        .addCase(fetchOrderInvoice.pending, (state) => {
            state.isInvoiceLoading = true;
        })
        .addCase(fetchOrderInvoice.fulfilled, (state) => {
            state.isInvoiceLoading = false;
        })
        .addCase(fetchOrderInvoice.rejected, (state, action) => {
            state.isInvoiceLoading = false;
            state.message = action.payload;
        })
        .addCase(initiatePayment.pending, (state) => {
            state.isLoading = true;
        })
        .addCase(initiatePayment.fulfilled, (state, action) => {
            state.isLoading = false;
            state.isSuccess = true;
            window.location.href = action.payload.redirectUrl;
        })
        .addCase(initiatePayment.rejected, (state, action) => {
            state.isLoading = false;
            state.isError = true;
            state.message = action.payload;
            toast.error(action.payload);
        })
        .addCase(getOrderPublic.pending, (state) => {
            state.isLoading = true;
        })
        .addCase(getOrderPublic.fulfilled, (state, action) => {
            state.isLoading = false;
            state.isSuccess = true;
            state.order = action.payload;
        })
        .addCase(getOrderPublic.rejected, (state, action) => {
            state.isLoading = false;
            state.isError = true;
            state.message = action.payload;
        })
        .addCase(toggleStatus.pending, (state) => {
            state.isLoading = true;
        })
        .addCase(toggleStatus.fulfilled, (state, action) => {
            state.isLoading = false;
            state.isSuccess = true;
            state.order.status = action.payload.status;
        })
        .addCase(toggleStatus.rejected, (state, action) => {
            state.isLoading = false;
            state.isError = true;
            state.message = action.payload;
        })
        .addCase(toggleStatusMultiple.pending, (state) => {
            state.isLoading = true;
        })
        .addCase(toggleStatusMultiple.fulfilled, (state, action) => {
            state.isLoading = false;
            state.isSuccess = true;
            state.orders = state.orders.map((order) => {
                if (action.payload.ids.includes(order._id)) {
                    order.status = action.payload.status;
                }
                return order;
            });
        })
        .addCase(toggleStatusMultiple.rejected, (state, action) => {
            state.isLoading = false;
            state.isError = true;
            state.message = action.payload;
        })
        .addCase(contactUs.pending, (state) => {
            state.isLoading = true;
        })
        .addCase(contactUs.fulfilled, (state) => {
            state.isLoading = false;
            state.isSuccess = true;
            toast.success(currentLang === 'en' ? 'Thank you for your message! We will contact you soon.' : 'Paldies par jūsu ziņu! Mēs ar jums sazināsimies drīzumā.');
        })
        .addCase(contactUs.rejected, (state, action) => {
            state.isLoading = false;
            state.isError = true;
            state.message = action.payload;
            toast.error(currentLang === 'en' ? 'Something went wrong. Please try again.' : 'Kaut kas nogāja greizi. Lūdzu, mēģiniet vēlreiz.');
        })
        .addCase(resendEmail.pending, (state) => {
            state.isLoading = true;
        })
        .addCase(resendEmail.fulfilled, (state) => {
            state.isLoading = false;
            state.isSuccess = true;
            toast.success(currentLang === 'en' ? 'You will receive a confirmation email soon.' : 'Jūs drīz saņemsiet apstiprinājuma e-pastu.');
        })
        .addCase(resendEmail.rejected, (state, action) => {
            state.isLoading = false;
            state.isError = true;
            state.message = action.payload;
            toast.error(currentLang === 'en' ? 'We have received your order, but could not send a confirmation email. Please contact us.' : 'Jūsu pasūtījumu esam saņēmuši, bet nevarējām nosūtīt apstiprinājuma e-pastu. Lūdzu sazinieties ar mums.');
        })
        .addCase(retryFulfillment.pending, (state) => {
            state.isLoading = true;
        })
        .addCase(retryFulfillment.fulfilled, (state, action) => {
            state.isLoading = false;
            state.isSuccess = true;
            toast.success(currentLang === 'en' ? 'Retry completed.' : 'Atkārtojums veiksmīgi pabeigts.');
        })
        .addCase(retryFulfillment.rejected, (state, action) => {
            state.isLoading = false;
            state.isError = true;
            state.message = action.payload;
            toast.error(action.payload);
        })
        .addCase(fetchShippingLabels.pending, (state) => {
            state.isLoading = true;
        })
        .addCase(fetchShippingLabels.fulfilled, (state) => {
            state.isLoading = false;
            state.isSuccess = true;
        })
        .addCase(fetchShippingLabels.rejected, (state, action) => {
            state.isLoading = false;
            state.isError = true;
            state.message = action.payload;
            toast.error(action.payload);
        })
        .addCase(exportOrders.pending, (state) => {
            state.isLoading = true;
        })
        .addCase(exportOrders.fulfilled, (state, action) => {
            state.isLoading = false;
            state.isSuccess = true;
            downloadExcel(action.payload, 'orders.xlsx');
        })
        .addCase(exportOrders.rejected, (state, action) => {
            state.isLoading = false;
            state.isError = true;
            state.message = action.payload;
            toast.error(action.payload);
        })
        .addCase(createCustomInvoice.pending, (state) => {
            state.isLoading = true;
        })
        .addCase(createCustomInvoice.fulfilled, (state, action) => {
            state.isLoading = false;
            state.isSuccess = true;
            downloadInvoice(action.payload, 'invoice.pdf');
        })
        .addCase(createCustomInvoice.rejected, (state, action) => {
            state.isLoading = false;
            state.isError = true;
            state.message = action.payload;
            toast.error(action.payload);
        })
        .addCase(getReviewObject.pending, (state) => {
            state.isLoading = true;
        })
        .addCase(getReviewObject.fulfilled, (state, action) => {
            state.isLoading = false;
            state.isSuccess = true;
            if (action.payload.completed && !state.warningShown) {
                toast.warning(currentLang === 'en' ? 'You have already left a review for this order.' : 'Jūs jau esat atstājis atsauksmi par šo pasūtījumu.');
              state.warningShown = true; // Set the flag to prevent duplicate warnings
            }
            state.review = action.payload;
        })
        .addCase(getReviewObject.rejected, (state, action) => {
            state.isLoading = false;
            state.isError = true;
            state.message = action.payload;
            toast.error(action.payload);
        })
        .addCase(postReview.pending, (state) => {
            state.isLoading = true;
        })
        .addCase(postReview.fulfilled, (state, action) => {
            state.isLoading = false;
            state.isSuccess = true;
            state.review = action.payload;
            toast.success(currentLang === 'en' ? 'Thank you, your review has been submitted!' : 'Paldies, Tava atsauksme ir pieņemta!');
        })
        .addCase(postReview.rejected, (state, action) => {
            state.isLoading = false;
            state.isError = true;
            state.message = action.payload;
            toast.error(action.payload);
        })
        .addCase(deleteReview.pending, (state) => {
            state.isLoading = true;
        })
        .addCase(deleteReview.fulfilled, (state, action) => {
            state.isLoading = false;
            state.isSuccess = true;
            state.review = null;
            toast.success(currentLang === 'en' ? 'Review successfully deleted!' : 'Atsauksme veiksmīgi izdzēsta!');
        })
        .addCase(deleteReview.rejected, (state, action) => {
            state.isLoading = false;
            state.isError = true;
            state.message = action.payload;
            toast.error(action.payload);
        })
        .addCase(updateReviewReply.pending, (state) => {
            state.isLoading = true;
        })
        .addCase(updateReviewReply.fulfilled, (state, action) => {
            state.isLoading = false;
            state.isSuccess = true;
            toast.success(currentLang === 'en' ? 'Review reply successfully updated, refresh screen!' : 'Atsauksmes atbilde veiksmīgi atjaunināta, refresh screen!');
        })
        .addCase(updateReviewReply.rejected, (state, action) => {
            state.isLoading = false;
            state.isError = true;
            state.message = action.payload;
            toast.error(action.payload);
        });
        
    },
  })
  
  export const { reset } = orderSlice.actions
  export default orderSlice.reducer
  
